import styled from "@emotion/styled";
import memoize from "memoize-one";
import * as React from "react";

import { DesignToken, ShadowToken } from "@livechat/design-system-react-components";

import { Icon, IconsEnum } from "components/icon/Icon";
import { IWithBreakpointsProps, withBreakpoints } from "helpers/withBreakpoints";
import { withVar } from "themes/helpers";

import { ModalPresenter } from "./ModalPresenter";

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/naming-convention
interface ModalProps extends IWithBreakpointsProps {
  title?: string;
  width: string;
  height: string;
  onClose(): void;
  closeOnEscClick?: boolean;
  isOpen?: boolean;
  allowOverflow?: boolean;
  children: React.ReactNode;
}

interface IStyledModalProps {
  width: string;
  height: string;
  maxHeight?: string;
  allowOverflow?: boolean;
  className?: string;
}

const StyledModal = styled.div<IStyledModalProps>`
  position: relative;
  display: flex;
  flex-direction: column;
  width: ${
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
    (props: any) => props.width
  };
  height: ${
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
    (props: any) => props.height
  };
  max-height: ${
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
    (props: any) => props.maxHeight
  };
  margin: 10px;
  border-radius: 4px;
  box-shadow: ${withVar(ShadowToken.Modal)};
  overflow: ${
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
    (props: any) => (props.allowOverflow ? "auto" : "hidden")
  };

  &,
  & * {
    box-sizing: border-box;
  }
`;

const StyledModalHeader = styled.h2`
  padding: 20px;
  color: ${withVar(DesignToken.ContentBasicPrimary)};
  font-size: 18px;
  font-weight: 600;
  line-height: 22px;
  text-align: center;
  border-bottom: 1px solid ${withVar(DesignToken.BorderBasicSecondary)};
  background-color: ${withVar(DesignToken.SurfacePrimaryDefault)};
  box-sizing: border-box;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
`;

const StyledModalCloseButton = styled.button`
  position: absolute;
  z-index: 1;
  right: 18px;
  top: 18px;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  background-color: transparent;
  box-shadow: none;
  opacity: 0.7;
  outline: none;

  &:hover {
    opacity: 1;
  }
`;

const StyledModalBody = styled.div`
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background-color: ${withVar(DesignToken.SurfaceSecondaryDefault)};
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;

  #new-modal-content {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;

    > div {
      height: 100%;
    }
  }
`;

const ESC_KEY_CODE = 27;

class ModalView extends React.PureComponent<ModalProps> {
  static defaultProps = {
    closeOnEscClick: true,
    isOpen: true,
  };

  private modalRef: React.RefObject<HTMLDivElement> = React.createRef();

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  componentDidMount() {
    document.addEventListener("keyup", this.onKeyUp, true);
  }

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  componentWillUnmount() {
    document.removeEventListener("keyup", this.onKeyUp, true);
  }

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  onPresenterClick = (event: React.MouseEvent<HTMLDivElement>) => {
    // @ts-expect-error TS(2531): Object is possibly 'null'.
    if (this.modalRef && !this.modalRef.current.contains(event.target as Node)) {
      this.handleCloseModal();
    }
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  onCloseButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    this.handleCloseModal();
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  onKeyUp = (event: KeyboardEvent) => {
    if (event.keyCode === ESC_KEY_CODE && this.props.closeOnEscClick) {
      this.handleCloseModal();
    }
  };

  getMaxHeight = memoize((isMobile: boolean, isTablet: boolean) => {
    if (isMobile) {
      return "60%";
    }
    if (isTablet) {
      return "80%";
    }
    return "90%";
  });

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  handleCloseModal = () => {
    this.props.onClose();
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  render() {
    const { title, isOpen, width, height, children, allowOverflow, isMobile, isTablet } = this.props;
    const maxHeight = allowOverflow ? this.getMaxHeight(isMobile, isTablet) : "90%";

    return (
      // @ts-expect-error TS(2322)
      <ModalPresenter isOpen={isOpen} onClick={this.onPresenterClick}>
        <StyledModal
          width={width}
          height={height}
          ref={this.modalRef}
          allowOverflow={allowOverflow}
          maxHeight={maxHeight}
        >
          {!!title && <StyledModalHeader>{title}</StyledModalHeader>}
          <StyledModalCloseButton title="Close modal" type="button" onClick={this.onCloseButtonClick}>
            <Icon name={IconsEnum.Close} fill="#000000" width="14px" height="14px" />
          </StyledModalCloseButton>
          <StyledModalBody>{children || <div id="new-modal-content" />}</StyledModalBody>
        </StyledModal>
      </ModalPresenter>
    );
  }
}

export const Modal = withBreakpoints(ModalView);
